/**
 * Non-intrusive user feedback prompt timing (web).
 *
 * Prefers server eligibility (survives logout) for warm-up / already-submitted.
 * Local storage (preserved on logout) only gates snooze / don't-ask / last show cap.
 *
 * - Warm-up: 2 days account age OR local firstSeen, AND ≥2 logins/sessions
 * - Delay: 45s (caller)
 * - Snooze: 7 days after “Maybe later”
 * - Auto re-show cap: 7 days
 * - After submit / don't-ask: never auto-prompt
 */

const STORAGE_PREFIX = 'userFeedback';

export const FEEDBACK_TIMING = {
  WARMUP_DAYS: 2,
  MIN_SESSIONS: 2,
  AUTO_DELAY_MS: 45_000,
  SNOOZE_DAYS: 7,
  MAX_ONCE_PER_DAYS: 7,
};

const key = (userId, field) => `${STORAGE_PREFIX}.${userId}.${field}`;

const read = (userId, field) => {
  try {
    return localStorage.getItem(key(userId, field));
  } catch {
    return null;
  }
};

const write = (userId, field, value) => {
  try {
    localStorage.setItem(key(userId, field), String(value));
  } catch {
    /* ignore quota */
  }
};

const daysBetween = (isoA, isoB = new Date().toISOString()) => {
  const a = new Date(isoA).getTime();
  const b = new Date(isoB).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return (b - a) / (1000 * 60 * 60 * 24);
};

/** Call once per app session after login. */
export function trackFeedbackSession(userId) {
  if (!userId) return;
  if (!read(userId, 'firstSeen')) {
    write(userId, 'firstSeen', new Date().toISOString());
  }
  const today = new Date().toISOString().slice(0, 10);
  if (read(userId, 'sessionCountedToday') === today) return;
  write(userId, 'sessionCountedToday', today);
  const count = parseInt(read(userId, 'sessionCount') || '0', 10) + 1;
  write(userId, 'sessionCount', count);
}

export function markFeedbackSubmitted(userId) {
  if (!userId) return;
  write(userId, 'submitted', '1');
}

export function markFeedbackSnoozed(userId) {
  if (!userId) return;
  const until = new Date();
  until.setDate(until.getDate() + FEEDBACK_TIMING.SNOOZE_DAYS);
  write(userId, 'snoozeUntil', until.toISOString());
}

export function markFeedbackDontAsk(userId) {
  if (!userId) return;
  write(userId, 'dontAsk', '1');
}

export function markFeedbackAutoShown(userId) {
  if (!userId) return;
  write(userId, 'lastAutoShown', new Date().toISOString());
}

/**
 * @param {string} userId
 * @param {{
 *   hasSubmittedBefore?: boolean,
 *   canAutoPrompt?: boolean,
 *   accountAgeDays?: number,
 *   loginSessionCount?: number,
 * }} serverEligibility
 */
export function shouldAutoPromptFeedback(userId, serverEligibility = {}) {
  if (!userId) return false;

  const hasSubmitted =
    serverEligibility.hasSubmittedBefore === true ||
    read(userId, 'submitted') === '1';
  if (hasSubmitted) return false;

  if (read(userId, 'dontAsk') === '1') return false;

  const snoozeUntil = read(userId, 'snoozeUntil');
  if (snoozeUntil && new Date(snoozeUntil) > new Date()) return false;

  const lastAuto = read(userId, 'lastAutoShown');
  if (lastAuto && daysBetween(lastAuto) < FEEDBACK_TIMING.MAX_ONCE_PER_DAYS) return false;

  // Server already decided account is warm enough
  if (serverEligibility.canAutoPrompt === true) return true;
  if (serverEligibility.canAutoPrompt === false) return false;

  // Fallback: local + any server stats we got
  const accountAgeDays =
    typeof serverEligibility.accountAgeDays === 'number'
      ? serverEligibility.accountAgeDays
      : (read(userId, 'firstSeen') ? daysBetween(read(userId, 'firstSeen')) : 0);

  const sessions =
    typeof serverEligibility.loginSessionCount === 'number'
      ? serverEligibility.loginSessionCount
      : parseInt(read(userId, 'sessionCount') || '0', 10);

  if (accountAgeDays < FEEDBACK_TIMING.WARMUP_DAYS) return false;
  if (sessions < FEEDBACK_TIMING.MIN_SESSIONS) return false;

  return true;
}
